import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { buildApiUrl } from '../../config/api';
import { fetchJson } from '../../utils/fetchJson';
import { getUnreadMessagesCount } from '../../utils/messages';

import { FiEye, FiHeart, FiUsers, FiMessageSquare, FiBarChart2, FiUser, FiRefreshCw } from 'react-icons/fi';

const DashboardHome = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Fetch dashboard statistics from backend
  useEffect(() => {
    fetchDashboardStats();
    // Also refresh stats periodically to get latest likes
    const interval = setInterval(fetchDashboardStats, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const { response, data } = await fetchJson(buildApiUrl('/api/dashboard/stats'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok && data.success) {
        setDashboardStats(data.data);
        console.log('📊 Dashboard stats fetched:', data.data);
      } else {
        setError(data.message || 'Failed to fetch dashboard statistics');
      }
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Calculate profile completion percentage (fallback if not provided by backend)
  const calculateProfileCompletion = () => {
    if (!user) return 0;

    const requiredFields = [
      'firstName', 'lastName', 'email', 'phone', 'gender',
      'dateOfBirth', 'religion', 'country', 'city',
      'education', 'profession'
    ];

    const optionalFields = [
      'community', 'profilePhoto'
    ];

    let completedFields = 0;
    let totalFields = requiredFields.length + optionalFields.length;

    // Check required fields
    requiredFields.forEach(field => {
      if (user[field] && user[field] !== '') {
        completedFields++;
      }
    });

    // Check optional fields
    optionalFields.forEach(field => {
      if (user[field] && user[field] !== '') {
        completedFields++;
      }
    });

    const percentage = Math.round((completedFields / totalFields) * 100);
    return Math.min(percentage, 100);
  };

  const profileCompletion = dashboardStats?.profileCompletion || calculateProfileCompletion();
  const unreadCount = getUnreadMessagesCount([{ unread: dashboardStats?.unreadMessages }]);

  // Format stats for display
  const getStatsData = () => {
    if (dashboardStats) {
      return [
        {
          label: 'Profile Views',
          value: dashboardStats.profileViews || 0,
          change: '+5',
          icon: <FiEye size={20} />
        },
        {
          label: 'Likes Received',
          value: dashboardStats.likesReceived || 0,
          change: '+2',
          icon: <FiHeart size={20} />
        },
        {
          label: 'New Matches',
          value: dashboardStats.newMatches || 0,
          change: '+3',
          icon: <FiUsers size={20} />
        },
        {
          label: 'Messages',
          value: unreadCount,
          change: '+7',
          icon: <FiMessageSquare size={20} />
        },
      ];
    }

    // Fallback dummy data
    return [
      { label: 'Profile Views', value: '0', change: '+0', icon: <FiEye size={20} /> },
      { label: 'Likes Received', value: '0', change: '+0', icon: <FiHeart size={20} /> },
      { label: 'New Matches', value: '0', change: '+0', icon: <FiUsers size={20} /> },
      { label: 'Messages', value: '0', change: '+0', icon: <FiMessageSquare size={20} /> },
    ];
  };

  const stats = getStatsData();

  // Format recent activities
  const getRecentActivities = () => {
    if (dashboardStats?.recentActivity && dashboardStats.recentActivity.length > 0) {
      return dashboardStats.recentActivity.map(activity => {
        const userNames = activity.message.split(' ');
        const userName = userNames.slice(0, 2).join(' '); // Get first two words as user name

        return {
          type: activity.type,
          user: userName,
          time: formatTimeAgo(activity.date),
          action: getActivityAction(activity.type),
          message: activity.message
        };
      });
    }

    // Fallback empty state
    return [];
  };

  const getActivityAction = (type) => {
    switch (type) {
      case 'profile_view': return 'viewed your profile';
      case 'like_received': return 'liked your profile';
      case 'message_received': return 'sent you a message';
      case 'new_match': return 'is a new match';
      default: return 'interacted with your profile';
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now - date;
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    return date.toLocaleDateString();
  };

  const recentActivities = getRecentActivities();

  return (
    <div className="max-w-7xl mx-auto">
      {/* Welcome Section */}
      <div
        className={`rounded-2xl p-8 text-white mb-8 border-4 transition-all duration-300 ${theme === 'dark'
          ? 'bg-transparent border-white bg-none'
          : 'bg-gradient-to-r from-romantic-500 to-primary-600 border-transparent'
          }`}
        style={theme === 'dark' ? { background: 'transparent', backgroundImage: 'none', borderColor: '#ffffff' } : {}}
      >
        <h1 className="text-3xl font-romantic font-bold mb-2">
          Welcome to Your Dashboard!
        </h1>
        <p className="text-rose-100 mb-4">
          Your journey to find the perfect life partner continues. Complete your profile to get better matches.
        </p>
        <div className="flex flex-wrap gap-4">
          <div className="bg-white bg-opacity-20 rounded-lg px-4 py-2">
            <span className="font-semibold">Profile Completion:</span> {profileCompletion}%
          </div>
          <button
            onClick={() => navigate('/dashboard/profile')}
            className="bg-white text-romantic-600 px-6 py-2 rounded-lg font-semibold hover:bg-rose-50 transition-colors duration-200"
          >
            Complete Profile
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {loading ? (
          // Loading skeleton for stats
          [1, 2, 3, 4].map((index) => (
            <div key={index} className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <div className="animate-pulse">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                  <div className="w-12 h-6 bg-gray-200 rounded-full"></div>
                </div>
                <div className="w-16 h-8 bg-gray-200 rounded mb-2"></div>
                <div className="w-24 h-4 bg-gray-200 rounded"></div>
              </div>
            </div>
          ))
        ) : (
          stats.map((stat, index) => (
            <div key={index} className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <div className="text-2xl">{stat.icon}</div>
                <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                  {stat.change}
                </span>
              </div>
              <div className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</div>
              <div className="text-gray-600 text-sm">{stat.label}</div>
            </div>
          ))
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Activity */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Activity</h2>

          {loading ? (
            // Loading skeleton for recent activity
            <div className="space-y-4">
              {[1, 2, 3, 4].map((index) => (
                <div key={index} className="flex items-center space-x-4 p-3">
                  <div className="animate-pulse">
                    <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                  </div>
                  <div className="flex-1 animate-pulse">
                    <div className="w-48 h-4 bg-gray-200 rounded mb-2"></div>
                    <div className="w-24 h-3 bg-gray-200 rounded"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : recentActivities.length > 0 ? (
            <div className="space-y-4">
              {recentActivities.map((activity, index) => (
                <div key={index} className="flex items-center space-x-4 p-3 hover:bg-gray-50 rounded-lg transition-colors duration-200">
                  <div className="w-10 h-10 bg-gradient-to-r from-romantic-100 to-primary-100 rounded-full flex items-center justify-center">
                    <span className="text-lg">
                      {activity.type === 'like_received' && <FiHeart size={16} />}
                      {activity.type === 'profile_view' && <FiEye size={16} />}
                      {activity.type === 'message_received' && <FiMessageSquare size={16} />}
                      {activity.type === 'new_match' && <FiUsers size={16} />}
                      {!['like_received', 'profile_view', 'message_received', 'new_match'].includes(activity.type) && <FiBarChart2 size={16} />}
                    </span>
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-900 font-medium">
                      <span className="text-romantic-600">{activity.user}</span> {activity.action}
                    </p>
                    <p className="text-gray-500 text-sm">{activity.time}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Empty state for recent activity
            <div className="text-center py-8 text-gray-500">
              <div className="flex justify-center mb-4"><FiBarChart2 size={48} /></div>
              <h3 className="text-lg font-semibold mb-2 text-gray-900">No recent activity</h3>
              <p className="text-sm">Your activity will appear here as you interact with the platform</p>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 gap-4">
            <Link to="/dashboard/messages" className="bg-gradient-to-br from-romantic-50 to-primary-50 rounded-xl p-4 text-center hover:from-romantic-100 hover:to-primary-100 transition-all duration-200 border-2 border-transparent hover:border-romantic-200">
              <div className="flex justify-center mb-2"><FiMessageSquare size={24} /></div>
              <div
                className="font-semibold text-gray-900"
                style={theme === 'dark' ? { color: '#0f172a' } : {}}
              >
                Messages
              </div>
              <div
                className="text-gray-600 text-sm"
                style={theme === 'dark' ? { color: '#1f2937' } : {}}
              >
                {unreadCount} unread
              </div>
            </Link>

            <Link to="/dashboard/profile" className="bg-gradient-to-br from-romantic-50 to-primary-50 rounded-xl p-4 text-center hover:from-romantic-100 hover:to-primary-100 transition-all duration-200 border-2 border-transparent hover:border-romantic-200">
              <div className="flex justify-center mb-2"><FiUser size={24} /></div>
              <div
                className="font-semibold text-gray-900"
                style={theme === 'dark' ? { color: '#0f172a' } : {}}
              >
                My Profile
              </div>
              <div
                className="text-gray-600 text-sm"
                style={theme === 'dark' ? { color: '#1f2937' } : {}}
              >
                {profileCompletion}% complete
              </div>
            </Link>


          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mt-8 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <FiRefreshCw className="w-5 h-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
            <button
              onClick={fetchDashboardStats}
              className="ml-auto text-red-600 hover:text-red-800 font-medium"
            >
              Retry
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardHome;